import { Box, CircularProgress, IconButton, Stack, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FoodsIcon, FoodsTooltip } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { PuckPage } from "src/api/client-api/cms/useCreatePuckPage";

import { getPublicationDescription } from "./publicationPuckPdfUtils";
import { actionButtonSx, cardSx } from "./styles";

type PublicationCardProps = {
    page: PuckPage;
    isAvailable: boolean;
    scoreTitle: string;
    isDownloading: boolean;
    onDownload: (page: PuckPage) => Promise<void>;
    onPreview: (page: PuckPage) => void;
};

export const PublicationCard = (props: PublicationCardProps) => {
    const { page, isAvailable, scoreTitle, isDownloading, onDownload, onPreview } = props;
    const { t } = useTranslation();

    const description = getPublicationDescription(page);

    const canDownload = isAvailable;
    const canPreview = isAvailable;

    const notAvailableTooltip = (
        <Box padding="8px" textAlign="left">
            <Typography variant="h4" sx={{ marginBottom: "8px" }}>
                {t("publications.tooltip.not_available_title", "Not available")}
            </Typography>
            <Typography variant="body-bold" color="text.secondary">
                {t(
                    "publications.tooltip.ssq_awaiting_validation",
                    "The document will be available once the questionnaire for this score is validated.",
                )}
            </Typography>
        </Box>
    );

    return (
        <Box sx={cardSx}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap="16px">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        noWrap
                        sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            lineHeight: 1.6,
                            color: "#393A36",
                        }}
                    >
                        {page.title}
                    </Typography>
                    {description !== "" && (
                        <Typography
                            noWrap
                            sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                lineHeight: 1.6,
                                color: "#767673",
                            }}
                        >
                            {description}
                        </Typography>
                    )}
                </Box>

                {isAvailable ?
                    <Box
                        sx={{
                            height: 22,
                            px: "8px",
                            py: "2px",
                            borderRadius: "4px",
                            background: "#DFFFE6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1.6,
                                color: "#12A332",
                            }}
                        >
                            {t("publications.status.available", "Available")}
                        </Typography>
                    </Box>
                :   <FoodsTooltip
                        sx={{
                            "& .MuiTooltip-tooltip": {
                                maxHeight: "unset",
                                height: "unset",
                            },
                        }}
                        arrow
                        placement="top"
                        title={notAvailableTooltip}
                    >
                        <Box
                            sx={{
                                height: 22,
                                px: "8px",
                                py: "2px",
                                borderRadius: "4px",
                                background: "#F6F5F3",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <Stack direction="row" alignItems="center" justifyContent="center" gap="4px">
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        lineHeight: 1.6,
                                        color: "#50504d",
                                    }}
                                >
                                    {t("publications.status.not_available", "Not available")}
                                </Typography>
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: "#50504d" }} />
                            </Stack>
                        </Box>
                    </FoodsTooltip>
                }
            </Stack>

            <Stack direction="row" alignItems="center" gap="16px" sx={{ minWidth: 0 }}>
                <Typography
                    noWrap
                    sx={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: 1.6,
                        color: "#767673",
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {t("publications.score", "Score: {{score}}", {
                        score: scoreTitle || t("common.all", "All"),
                    })}
                </Typography>

                <Stack direction="row" alignItems="center" gap="12px">
                    <IconButton
                        onClick={() => onDownload(page)}
                        disabled={!canDownload || isDownloading}
                        sx={{
                            ...actionButtonSx,
                            background: canDownload ? actionButtonSx.background : "#FFFFFF",
                            opacity: canDownload ? 1 : 0.36,
                        }}
                    >
                        {isDownloading ?
                            <CircularProgress size={18} />
                        :   <FoodsIcon icon="exportDownload" size={2} />}
                    </IconButton>

                    <IconButton
                        onClick={() => {
                            if (!canPreview) return;
                            onPreview(page);
                        }}
                        disabled={!canPreview}
                        sx={{
                            ...actionButtonSx,
                            background: canPreview ? actionButtonSx.background : "#FFFFFF",
                            opacity: canPreview ? 1 : 0.36,
                        }}
                    >
                        <FoodsIcon icon="viewPwd" size={2} />
                    </IconButton>
                </Stack>
            </Stack>
        </Box>
    );
};
